import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Basketball, SignOut, Users, Trophy, CalendarBlank, FilmSlate, ClipboardText,
  ChartBar, House, GearSix, Timer, ClockCounterClockwise, ShieldCheck, Handshake, Star, BookOpen, TreeStructure, CalendarPlus, Crosshair,
} from "@phosphor-icons/react";
import Overview from "./admin/Overview";
import ScoreKeeperTab from "./admin/ScoreKeeperTab";
import TeamsTab from "./admin/TeamsTab";
import PlayersTab from "./admin/PlayersTab";
import ScheduleTab from "./admin/ScheduleTab";
import RegistrationsTab from "./admin/RegistrationsTab";
import HighlightsTab from "./admin/HighlightsTab";
import SettingsTab from "./admin/SettingsTab";
import ActivityLogTab from "./admin/ActivityLogTab";
import ComplianceTab from "./admin/ComplianceTab";
import SponsorsTab from "./admin/SponsorsTab";
import PlayerOfGameTab from "./admin/PlayerOfGameTab";
import RulesTab from "./admin/RulesTab";
import PlayoffsTab from "./admin/PlayoffsTab";
import SeasonsTab from "./admin/SeasonsTab";
import ShootoutTab from "./admin/ShootoutTab";

const TABS = [
  { key: "overview", label: "Overview", icon: ChartBar },
  { key: "scorekeeper", label: "Score Keeper", icon: Timer },
  { key: "teams", label: "Teams", icon: Users },
  { key: "players", label: "Players", icon: Trophy },
  { key: "compliance", label: "Compliance", icon: ShieldCheck },
  { key: "schedule", label: "Schedule", icon: CalendarBlank },
  { key: "playoffs", label: "Playoffs", icon: TreeStructure },
  { key: "shootout", label: "3PT Shootout", icon: Crosshair },
  { key: "registrations", label: "Registrations", icon: ClipboardText },
  { key: "highlights", label: "Highlights", icon: FilmSlate },
  { key: "rules", label: "IEFSC Rules", icon: BookOpen },
  { key: "potg", label: "Player of Game", icon: Star },
  { key: "sponsors", label: "Sponsors", icon: Handshake },
  { key: "activity", label: "Activity Log", icon: ClockCounterClockwise },
  { key: "settings", label: "Settings", icon: GearSix },
  { key: "seasons", label: "Seasons", icon: CalendarPlus },
];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (admin === false) navigate("/admin");
  }, [admin, navigate]);

  if (!admin) return <div className="min-h-screen flex items-center justify-center text-white/50">Loading...</div>;

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-64 border-b md:border-b-0 md:border-r border-white/10 bg-[#0A0A0A] flex md:flex-col">
        <div className="hidden md:flex items-center gap-2 px-6 h-20 border-b border-white/10">
          <span className="w-9 h-9 bg-[#FF5722] flex items-center justify-center"><Basketball size={22} weight="fill" className="text-black" /></span>
          <span className="font-display text-2xl">ADMIN</span>
        </div>
        <nav className="flex md:flex-col flex-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-3 px-6 py-4 text-sm font-semibold uppercase tracking-wide whitespace-nowrap transition-all duration-200 ${
                tab === t.key ? "bg-[#FF5722] text-black" : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <t.icon size={18} weight={tab === t.key ? "fill" : "regular"} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="hidden md:flex flex-col gap-1 p-3 border-t border-white/10">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 px-3 py-2 text-white/50 hover:text-white text-sm transition-colors"><House size={16} /> View Site</button>
          <button data-testid="logout-button" onClick={() => { logout(); navigate("/admin"); }} className="flex items-center gap-2 px-3 py-2 text-white/50 hover:text-[#FF3B30] text-sm transition-colors"><SignOut size={16} /> Logout</button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 bg-[#0A0A0A] p-6 sm:p-10 overflow-x-hidden">
        {tab === "overview" && <Overview />}
        {tab === "scorekeeper" && <ScoreKeeperTab />}
        {tab === "teams" && <TeamsTab />}
        {tab === "players" && <PlayersTab />}
        {tab === "compliance" && <ComplianceTab />}
        {tab === "schedule" && <ScheduleTab />}
        {tab === "playoffs" && <PlayoffsTab />}
        {tab === "shootout" && <ShootoutTab />}
        {tab === "registrations" && <RegistrationsTab />}
        {tab === "highlights" && <HighlightsTab />}
        {tab === "rules" && <RulesTab />}
        {tab === "potg" && <PlayerOfGameTab />}
        {tab === "sponsors" && <SponsorsTab />}
        {tab === "activity" && <ActivityLogTab />}
        {tab === "settings" && <SettingsTab />}
        {tab === "seasons" && <SeasonsTab />}
      </main>
    </div>
  );
}
